import React, { useState } from 'react';
import { useAuthStore } from '../../store/authStore';
import { authService } from '../../services/apiService';
import { Modal } from '../ui/Modal';
import { Row, Section } from '../settingsPrimitives';
import { forgetPlanClaim } from '../../plans/planClaim';
import { PasswordInput } from './PasswordInput';

export const AccountDataSection = ({ t }) => {
  const logout = useAuthStore((s) => s.logout);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [password, setPassword]       = useState('');
  const [deleting, setDeleting]       = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);

  const handleOpenDelete = () => {
    setPassword('');
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const handleCloseDelete = () => {
    if (deleting) return;
    setShowDeleteModal(false);
    setPassword('');
    setDeleteError(null);
  };

  const handleDeleteAccount = async () => {
    if (!password) { setDeleteError('Please enter your password to confirm.'); return; }
    setDeleting(true);
    setDeleteError(null);
    try {
      await authService.deleteAccount(password);
      setDeleteSuccess(true);
      setShowDeleteModal(false);
      // The account is gone: its local git histories go with it (05.10.2026),
      // and so does its cached signed plan claim (Plans v2, 16.10.2026), as
      // on "Remove from device".
      forgetPlanClaim();
      setTimeout(() => logout({ purge: true, reason: 'account_deleted', removeLocalHistories: true }), 1500);
    } catch (err) {
      setDeleteError(err.message || 'Could not delete account. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Section title={t('settings.deleteAccount')}>
        {deleteSuccess ? (
          <div className="privacy-feedback privacy-feedback--success">{t('settings.deleteAccountSuccess')}</div>
        ) : (
          <Row label="Delete your account" desc="Permanently removes your account and all associated data.">
            <button
              type="button"
              className="btn-destructive btn-destructive--ghost flex-shrink-0"
              onClick={handleOpenDelete}
            >
              Delete account
            </button>
          </Row>
        )}
      </Section>

      <Modal
        isOpen={showDeleteModal}
        onClose={handleCloseDelete}
        title="Delete account"
        type="danger"
        preventBackdropClose={deleting}
        preventEscapeClose={deleting}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text-2, #a1a1aa)' }}>
            This action is <strong style={{ color: 'var(--text-1, inherit)' }}>permanent and cannot be undone.</strong> All your workspaces, collections, and account data will be deleted immediately.
          </p>
          <p style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--text-2, #a1a1aa)' }}>
            {t('settings.deleteAccountLocalItems')}
          </p>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6, color: 'var(--text-2, #a1a1aa)' }}>
              Enter your password to confirm
            </label>
            <PasswordInput
              placeholder="Your password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setDeleteError(null); }}
              autoComplete="current-password"
              disabled={deleting}
              autoFocus
              style={{ width: '100%' }}
            />
            {deleteError && (
              <div className="privacy-feedback privacy-feedback--error" style={{ marginTop: 6 }}>{deleteError}</div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="settings-action-btn"
              onClick={handleCloseDelete}
              disabled={deleting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-destructive"
              onClick={handleDeleteAccount}
              disabled={deleting || !password}
            >
              {deleting ? 'Deleting…' : 'Delete my account'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};
